/**
 * Feature Formatter Utility
 *
 * Converts feature identifiers into human-readable, translated display names.
 * Used throughout the application for feature adoption analytics, environment info,
 * and chart configurations.
 *
 * @module featuresFormater
 *
 * @example
 * ```typescript
 * import { featuresFormater } from 'utils/featuresFormater';
 * import { useTranslation } from 'react-i18next';
 *
 * const { t } = useTranslation();
 * const displayName = featuresFormater('gsuiteEnabled', t);
 * // Returns: "G Suite" (translated)
 * ```
 *
 * @example
 * ```typescript
 * // Unknown feature keys return the original key
 * const displayName = featuresFormater('unknownFeature', t);
 * // Returns: "unknownFeature"
 * ```
 */

import type { TranslationFunction } from './featuresFormater.types';
import { FEATURE_TRANSLATION_MAP } from './featureMappings';

/**
 * Formats a feature identifier into a human-readable, translated display name
 *
 * @param item - The feature identifier/key to format
 * @param t - Translation function from react-i18next
 * @returns Translated feature display name, or the original item if not found in mapping
 *
 * @remarks
 * - If the feature key exists in FEATURE_TRANSLATION_MAP, returns the translated value
 * - If the feature key is not found, returns the original key as-is (fallback behavior)
 * - This maintains backward compatibility with dynamic or new feature keys
 *
 * @see {@link FEATURE_TRANSLATION_MAP} for all supported feature mappings
 */
export const featuresFormater = (
  item: string,
  t: TranslationFunction
): string => {
  const translationKey = FEATURE_TRANSLATION_MAP[item];

  // If mapping exists, return translated value
  if (translationKey) {
    return t(translationKey);
  }

  // Fallback: return original item if no mapping found
  // This maintains backward compatibility for dynamic or unknown features
  return item;
};
